import type { SchedulingAvailabilityInfo } from '@carvana/merch-contracts';
import { FulfillmentIdMap } from '@carvana/merch-contracts/FulfillmentType';
import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import type { Result } from '@carvana/merch-result';
import axios from 'axios';
import { useContext, useEffect, useRef, useState } from 'react';
import SortOption from '~/constants/common/SortOption';
import { GET_DELIVERY_PICKUP } from '~/constants/endpoints';
import DeviceContext from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import UserContext, { useIsLoggedIn } from '~/context/common/UserContext';
import type {
  AppliedFilters,
  RecommendedVehicle,
  RequestedFeature
} from '~/contracts/common';
import type { DeliveryPickupResponse } from '~/services/common/contracts';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';

const deliveryPickupNinetyNinthPercentile = 2200;

type VehicleAvailabilityData = Record<
  number,
  {
    soonestPickup: Result<SchedulingAvailabilityInfo>;
    soonestDelivery: Result<SchedulingAvailabilityInfo>;
  }
>;

function transformAvailabilityToObject(
  data: DeliveryPickupResponse
): VehicleAvailabilityData {
  return data.content
    ? data.content.reduce<VehicleAvailabilityData>((acc, item) => {
        acc[item.vehicleId] = item;

        return acc;
      }, {})
    : {};
}

function useGetVehiclesAndAvailability(filterToSet: Partial<AppliedFilters>) {
  const isLoggedIn = useIsLoggedIn();
  const { deviceType, browserName, deviceName } = useContext(DeviceContext);
  const { isMobile } = deviceType;
  const {
    favorites: { data: favorites = [] },
    location: {
      data: { zip = '' }
    }
  } = useContext(UserContext);
  const { clientId } = useContext(StaticContext);
  const {
    data: inventory,
    error: inventoryError,
    isLoaded: isInventoryLoaded,
    isLoading: isInventoryLoading,
    setData: setInventoryData,
    setError: setInventoryError,
    setLoading: setInventoryLoading
  } = useLoadableState<RecommendedVehicle[]>([]);
  const [isTradeInApplied, setIsTradeApplied] = useState(false);
  const [hasSoftpull, setHasSoftpull] = useState(false);
  const [availabilityData, setAvailabilityData] =
    useState<VehicleAvailabilityData>({});
  const mounted = usePromise();
  const favoritesRef = useRef<number[]>([]);
  const isMobileRef = useRef(isMobile);
  const didCallAvailabilityRef = useRef(false);
  const shouldFetchAvailability =
    isInventoryLoaded && !inventoryError && inventory.length !== 0;

  useEffect(() => {
    const favoritesIds = favorites.map(favorite => favorite.vehicleId);
    setInventoryData(prevVehicles =>
      prevVehicles.map(vehicle => ({
        ...vehicle,
        isFavorited: favoritesIds.includes(vehicle.vehicleId)
      }))
    );
    favoritesRef.current = favoritesIds;
  }, [favorites, setInventoryData]);

  useEffect(() => {
    favoritesRef.current = favorites.map(favorite => favorite.vehicleId);
  }, [favorites]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      if (!zip) return;

      const requestedFeaturesToUse = new Array<RequestedFeature>().concat([
        'ApplyTradeIn',
        'EarliestAcquisitionBoosting',
        'LoanTermPricing',
        'LocationBasedPrefiltering',
        'Personalization'
      ]);

      async function fetchVehicles() {
        setInventoryLoading();
        const { data } = await fetchInventory(
          {
            analyticsData: {
              browser: browserName ?? '',
              clientId: clientId,
              deviceName: deviceName ?? '',
              isMobileDevice: isMobileRef.current,
              referrer: document.referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: {
              ...filterToSet
            },
            pagination: { page: 1, pageSize: 12 },
            requestedFeatures: requestedFeaturesToUse,
            sortBy: SortOption.MostPopular,
            zip5: zip
          },
          controller
        );

        if (data.tradeInfo && data.tradeInfo.applyTradeIn != null) {
          setIsTradeApplied(data.tradeInfo.applyTradeIn);
        }

        if (data.financeInfo && data.financeInfo.financingType != null) {
          setHasSoftpull(data.financeInfo.financingType === 'userFinancing');
        }

        return data.inventory.vehicles;
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setInventoryData(
            vehicles.map<RecommendedVehicle>(vehicle => ({
              ...vehicle,
              isFavorited: favoritesRef.current.includes(vehicle.vehicleId)
            }))
          );
        })
        .catch(
          generateErrorLogger(
            'Get Ev vehicles',
            {},
            {
              callback: setInventoryError,
              controller
            }
          )
        );
    },
    [
      browserName,
      clientId,
      deviceName,
      filterToSet,
      isLoggedIn,
      mounted,
      setInventoryData,
      setInventoryError,
      setInventoryLoading,
      zip
    ]
  );

  useEffectWithController(
    controller => {
      if (!zip || !shouldFetchAvailability || didCallAvailabilityRef.current)
        return;

      didCallAvailabilityRef.current = true;

      async function getBulkAvailability() {
        const { data: availability } = await axios.post<DeliveryPickupResponse>(
          GET_DELIVERY_PICKUP,
          {
            vehicles: inventory.map(
              ({ fulfillmentType, locationId, vehicleId }) => ({
                VehicleLocationId: locationId,
                vehicleId,
                vehicleInventoryType: FulfillmentIdMap[fulfillmentType]
              })
            ),
            zipCode: zip
          },
          {
            headers: {
              browsercookieid: BrowserCookies.get('BrowserCookieId') ?? '',
              clientId,
              correlationId: ''
            },
            timeout: deliveryPickupNinetyNinthPercentile
          }
        );

        return transformAvailabilityToObject(availability);
      }

      mounted(getBulkAvailability())
        .then(setAvailabilityData)
        .catch(
          generateErrorLogger(
            'Get Ev vehicles availability',
            {},
            {
              controller
            }
          )
        );
    },
    [
      browserName,
      clientId,
      deviceName,
      inventory,
      isLoggedIn,
      mounted,
      setAvailabilityData,
      shouldFetchAvailability,
      zip
    ]
  );

  return {
    vehicles: {
      error: inventoryError,
      isLoading: isInventoryLoading,
      isLoaded: isInventoryLoaded,
      vehicles: inventory
    },
    availabilityData,
    hasSoftpull,
    isTradeInApplied
  };
}

export default useGetVehiclesAndAvailability;
